// Game Money hub (UX Phase 3, 2026-08-08).
//
// The owner's #1 pain was that everything deciding how much each game earns
// was scattered across 7 far-apart places — and the daily profit report +
// simulator had no menu home at all. This hub brings them together under one
// roof with tabs, so "see it → predict it → set it → economics → availability"
// is one tap each:
//
//   Profit        the daily 8-quarter Target-Profit report (house profit vs target)
//                 + (Phase B) the Spotlight sub-tab: who is winning, by district
//   Simulator     predictive Monte-Carlo preview of profit at chosen targets
//   Earnings      the master casino-profit preset + (Phase B) who wins — spotlight
//                 & fairness, directly beneath it + a jump to per-game sliders
//   Economics     rebate & fees + platform safety limits
//   Availability  show / hide / lock each game on the lobby + the developer PIN
//
// Each tab renders the SAME self-contained panel the standalone routes use
// (those routes still work as deep links / redirect targets), so there is no
// duplicated logic — just one consolidated view. The active tab is stored in
// the ?tab= query param so it can be deep-linked (e.g. the Home "profit" card
// opens ?tab=profit). Phase B adds ?sub=spotlight for the Profit sub-tab
// (plan §4.7: "ONE new sub-tab of the Profit tab named Spotlight").
import { useSearchParams, Link } from 'react-router-dom';
import Icon from '@baaligames/shared-ui/components/Icon';
import { SegmentedTabs, type TabDef } from '@baaligames/shared-ui/admin/components/TabHub';
import TargetProfitReport from './TargetProfitReport';
import TargetProfitSimulator from './TargetProfitSimulator';
import GameDevSecurity from './GameDevSecurity';
import MasterCasinoProfitPanel from '../panels/operations/MasterCasinoProfitPanel';
import AdrenalinePanel, { useAdrenalinePreset, spotlightActiveLabel } from '../panels/operations/AdrenalinePanel';
import ReservePanel from '../panels/operations/ReservePanel';
import SpotlightTab from '../panels/operations/SpotlightTab';
import RtpTab from '../panels/operations/RtpTab';
import RebatePanel from '../panels/operations/RebatePanel';
import PlatformLimitsPanel from '../panels/operations/PlatformLimitsPanel';

type TabKey = 'profit' | 'simulator' | 'earnings' | 'economics' | 'availability';
type ProfitSub = 'daily' | 'spotlight' | 'rtp';

const TABS: { key: TabKey; label: string; icon: string }[] = [
  { key: 'profit',       label: 'Profit',       icon: 'monitoring' },
  { key: 'simulator',    label: 'Simulator',    icon: 'insights' },
  { key: 'earnings',     label: 'Earnings',     icon: 'tune' },
  { key: 'economics',    label: 'Economics',    icon: 'cycle' },
  { key: 'availability', label: 'Availability', icon: 'visibility' },
];

// Profit sub-tabs (state-driven SegmentedTabs; persisted in ?sub=).
const PROFIT_SUBS: TabDef[] = [
  { key: 'daily',     label: 'Daily report', icon: 'monitoring' },
  { key: 'spotlight', label: 'Spotlight',    icon: 'auto_awesome' },
  // Phase 3 (2026-09-15). Lives HERE rather than as its own page: it answers the
  // same question as the daily report — what is this game earning — just cut by
  // stake and by player instead of by hour. A separate top-level page would be
  // scattering one subject across the nav.
  { key: 'rtp',       label: 'By stake / player', icon: 'insights' },
];

export default function AdminGameMoneyHub() {
  const [sp, setSp] = useSearchParams();
  const tab: TabKey = (TABS.find((t) => t.key === sp.get('tab'))?.key) ?? 'profit';
  const subParam = sp.get('sub');
  const sub: ProfitSub = subParam === 'spotlight' ? 'spotlight' : subParam === 'rtp' ? 'rtp' : 'daily';
  const setTab = (k: TabKey) =>
    setSp(k === 'profit' ? {} : { tab: k }, { replace: true });
  // Profit is the default tab, so "profit + daily" is the bare URL; only the
  // spotlight sub-tab needs both params (?tab=profit&sub=spotlight).
  const setSub = (k: string) =>
    setSp(k === 'daily' ? {} : { tab: 'profit', sub: k }, { replace: true });

  // Phase B: the hub owns the adrenaline GET so the master panel's ONE
  // "Now active:" line can read "… · Spotlight: Normal" and the AdrenalinePanel
  // renders from the same response (one fetch, one section). Only fetched
  // while the Earnings tab is open.
  const adrenaline = useAdrenalinePreset(tab === 'earnings');

  return (
    <div className="space-y-4">
      <header className="px-1">
        <h1 className="text-xl font-display font-extrabold uppercase tracking-wider
                       text-arena-800 dark:text-neon-100 inline-flex items-center gap-2">
          <Icon name="payments" size={22} filled className="text-emerald-600 dark:text-emerald-300" />
          Game money
        </h1>
        <p className="text-xs text-arena-500 dark:text-neon-200/60 mt-0.5">
          Everything that decides how much each game earns — in one place.
        </p>
      </header>

      {/* Scrollable tab strip — one row, scrolls horizontally on small phones
          so all five tabs stay reachable without wrapping the page. */}
      <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1
                      [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {TABS.map((t) => {
          const active = t.key === tab;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              aria-pressed={active}
              className={`shrink-0 inline-flex items-center gap-1.5 h-10 px-4 rounded-full
                          text-sm font-bold transition active:scale-[.98]
                          ${active
                            ? 'bg-emerald-500 text-white shadow-[0_2px_10px_rgba(16,185,129,0.4)]'
                            : 'bg-white/60 dark:bg-arena-800/60 text-arena-700 dark:text-neon-200/70 ' +
                              'border border-arena-200/60 dark:border-arena-600/40 hover:border-emerald-400/50'}`}
            >
              <Icon name={t.icon} size={16} filled /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Body — only the active tab's panel mounts (so only it fetches). */}
      {tab === 'profit' && (
        <div className="space-y-3">
          <SegmentedTabs tabs={PROFIT_SUBS} activeKey={sub} onTabChange={setSub} className="max-w-md" />
          {sub === 'daily' && <TargetProfitReport />}
          {sub === 'spotlight' && <SpotlightTab />}
          {sub === 'rtp' && <RtpTab />}
        </div>
      )}
      {tab === 'simulator' && <TargetProfitSimulator />}
      {tab === 'earnings' && (
        <div className="space-y-4">
          <MasterCasinoProfitPanel extraActive={spotlightActiveLabel(adrenaline)} />
          <AdrenalinePanel state={adrenaline} />
          {/* Phase 2 Stage 3.5 (2026-09-15): HOW the target above is delivered. Same
              family as the two panels over it — how much the house keeps, who wins,
              how it is delivered — so it lives on the same page rather than in a tab
              of its own (owner rule: consolidate, don't scatter). Deep link:
              /settings/reserve. */}
          <ReservePanel />
          <Link
            to="/matka"
            className="card p-4 flex items-center gap-3 active:scale-[.99] transition"
          >
            <div className="w-10 h-10 rounded-xl grid place-items-center shrink-0
                            bg-gradient-to-br from-amber-500/20 to-yellow-600/15
                            text-amber-700 dark:text-amber-300">
              <Icon name="casino" size={20} filled />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-arena-800 dark:text-neon-100">Matka King</div>
              <div className="text-xs text-arena-500 dark:text-neon-200/60">
                Markets, fixed multipliers, declare results by hand, the engine's book and the day's bets.
              </div>
            </div>
            <Icon name="chevron_right" size={22} className="text-arena-400 dark:text-neon-200/50 shrink-0" />
          </Link>
          <Link
            to="/settings/games"
            className="card p-4 flex items-center gap-3 active:scale-[.99] transition"
          >
            <div className="w-10 h-10 rounded-xl grid place-items-center shrink-0
                            bg-gradient-to-br from-violet-500/20 to-fuchsia-500/15
                            text-violet-700 dark:text-violet-300">
              <Icon name="sports_esports" size={20} filled />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-arena-800 dark:text-neon-100">Tune a specific game</div>
              <div className="text-xs text-arena-500 dark:text-neon-200/60">
                Open a game to set its own profit slider, bet limits and bot behaviour.
              </div>
            </div>
            <Icon name="chevron_right" size={22} className="text-arena-400 dark:text-neon-200/50 shrink-0" />
          </Link>
        </div>
      )}
      {tab === 'economics' && (
        <div className="space-y-4">
          <RebatePanel />
          <PlatformLimitsPanel />
        </div>
      )}
      {tab === 'availability' && <GameDevSecurity />}
    </div>
  );
}
